import { extractText } from '../codelists/lookup.js';
import { bzbFillsLayer } from '../codelists/index.js';
import { LayerModel } from './LayerModel.js';
import { MonsterModel } from './MonsterModel.js';
import type { JsonMeetpunt, Boring } from '../types/index.js';
import { unwrapName } from '../types/index.js';

export class BoringModel {
  readonly guid: string | null;
  readonly name: string;
  readonly type: string | null;
  readonly boormeester: string | null;
  readonly drillingSystem: string | null;
  readonly groundLevelType: string | null;
  readonly gwsFound: boolean | null;
  readonly gws: number | null;
  readonly coordinates: string | null;
  readonly date: string | null;
  readonly opmerking: string | null;
  readonly raw: JsonMeetpunt;

  readonly layers: LayerModel[];
  readonly monsters: MonsterModel[];
  readonly maxDepth: number; // metres

  constructor(data: JsonMeetpunt, allLayers: LayerModel[] = [], allMonsters: MonsterModel[] = [], fallbackDate?: string | null) {
    this.raw = data;
    this.guid        = extractText(data.guid) || null;
    this.name        = unwrapName(data.name ?? data.identificatie) ?? 'Naamloos';
    this.type        = extractText(data.typeOnderzoek) || null;
    this.boormeester = extractText(data.boormeester) || null;
    this.drillingSystem = extractText(data.boorsysteem) || null;
    this.groundLevelType = extractText(data.maaiveldtype) || null;
    this.gwsFound    = typeof data.gws_found?.waarde === 'boolean' ? data.gws_found.waarde : null;
    this.gws         = typeof data.gws_cm_mv?.waarde === 'number' ? data.gws_cm_mv.waarde : null;
    this.coordinates = extractText(data.coordinaten) || null;
    this.date        = extractText(data.datum) || fallbackDate || null;
    this.opmerking   = extractText(data.opmerking) || null;

    // Match layers by GUID (preferred) or by name
    this.layers = allLayers
      .filter(l =>
        (l.meetpuntGuid && this.guid && l.meetpuntGuid === this.guid) ||
        l.meetpuntName === this.name ||
        (!l.meetpuntName && this.name === 'Naamloos'),
      )
      .sort((a, b) => a.upperDepth - b.upperDepth);

    this.monsters = allMonsters.filter(m =>
      (m.meetpuntGuid && this.guid && m.meetpuntGuid === this.guid) ||
      m.meetpuntName === this.name,
    );

    this.maxDepth = this.layers.reduce((max, l) => Math.max(max, l.lowerDepth), 0) / 100;
  }

  /**
   * Construct a BoringModel from a normalized flat Boring DB row (with nested layers/monsters).
   * @param boring  Flat Boring object as returned by `GET /api/projects/:id/borings`
   */
  static fromFlat(boring: Boring & { layers?: any[]; monsters?: any[] }): BoringModel {
    const json: JsonMeetpunt = {
      guid: boring.id,
      name: { waarde: boring.name || '' },
      typeOnderzoek: boring.type ? { waarde: boring.type } : undefined,
      boormeester: boring.boormeester ? { waarde: boring.boormeester } : undefined,
      boorsysteem: boring.drillingSystem ? { waarde: boring.drillingSystem } : undefined,
      maaiveldtype: boring.groundLevelType ? { waarde: boring.groundLevelType } : undefined,
      gws_found: boring.gwsFound != null ? { waarde: boring.gwsFound } : undefined,
      gws_cm_mv: boring.gwsCmMv != null ? { waarde: boring.gwsCmMv } : undefined,
      datum: boring.datum ? { waarde: boring.datum } : undefined,
      opmerking: boring.opmerking ? { waarde: boring.opmerking } : undefined,
    };
    const layers = (boring.layers ?? []).map((l: any) => LayerModel.fromFlat(l, boring.name));
    const monsters = (boring.monsters ?? []).map((m: any) => MonsterModel.fromFlat(m));
    return new BoringModel(json, layers, monsters, boring.datum);
  }

  /** Consecutive depth gaps or overlaps between layers. */
  get depthConflicts(): { type: 'gap' | 'overlap'; afterIdx: number; a: LayerModel; b: LayerModel }[] {
    const conflicts: { type: 'gap' | 'overlap'; afterIdx: number; a: LayerModel; b: LayerModel }[] = [];
    for (let i = 0; i < this.layers.length - 1; i++) {
      const a = this.layers[i];
      const b = this.layers[i + 1];
      if (b.upperDepth > a.lowerDepth)      conflicts.push({ type: 'gap',     afterIdx: i, a, b });
      else if (b.upperDepth < a.lowerDepth) conflicts.push({ type: 'overlap', afterIdx: i, a, b });
    }
    return conflicts;
  }

  /** Validation warnings for this boring, suitable for display in the UI. */
  get warnings(): string[] {
    const result: string[] = [];
    for (const c of this.depthConflicts) {
      const a = `${c.a.upperDepth}–${c.a.lowerDepth} cm`;
      const b = `${c.b.upperDepth}–${c.b.lowerDepth} cm`;
      result.push(c.type === 'gap'
        ? `Diepte gap: ${a} → ${b}`
        : `Diepte overlap: ${a} ↔ ${b}`);
    }
    // Een laag die zelf uit een bestanddeel bestaat (lucht, ballast, puin) hoort geen
    // grondsoort te hebben; zie bzbFillsLayer.
    const noSoil = this.layers.filter(l => !l.soilType
      && !(l.bzb || []).some(b => (b?.type || b?.code) && bzbFillsLayer(b?.gradation)));
    if (noSoil.length > 0) {
      result.push(`${noSoil.length} laag${noSoil.length > 1 ? 'en' : ''} zonder grondsoort`);
    }
    return result;
  }
}
